import { queryOptions } from "@tanstack/react-query";

import type {
  DeclinedInvitation,
  InvitationBlock,
  PendingShareInvitation,
} from "@/lib/shares/invitations";
import type { ShareListItem } from "@/lib/services/shares";
import { HOME_WIDGET_QUERY_META } from "@/lib/home/widgets/query-lifecycle";

/**
 * The user's shares (owned + contributed) — one cache for the Partage list
 * and the home page's "À traiter", so both read the same records.
 */
export const sharesQueryOptions = queryOptions({
  queryKey: ["shares"],
  meta: HOME_WIDGET_QUERY_META,
  queryFn: async ({ signal }): Promise<ShareListItem[]> => {
    const res = await fetch("/api/shares", { cache: "no-store", signal });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // SAFETY: GET /api/shares returns { groups: ShareListItem[] } per the
    // route's documented response contract.
    const data = (await res.json()) as { groups: ShareListItem[] };

    return data.groups;
  },
});

/** App-wide notice: a small read that stays active when the homepage is left. */
export const pendingShareInvitationsQueryOptions = queryOptions({
  queryKey: ["shares", "pending-invitations"],
  queryFn: async ({ signal }): Promise<PendingShareInvitation[]> => {
    const res = await fetch("/api/shares/pending-invitations", { cache: "no-store", signal });

    if (!res.ok) throw new Error("Impossible de charger les invitations.");

    // SAFETY: the session route returns only the caller's PendingShareInvitation rows.
    return (await res.json()) as PendingShareInvitation[];
  },
});

/**
 * The user's declined invitations — under ["shares"] so answering or any
 * share mutation refreshes it with the list.
 */
export const declinedInvitationsQueryOptions = queryOptions({
  queryKey: ["shares", "declined-invitations"],
  queryFn: async (): Promise<DeclinedInvitation[]> => {
    const res = await fetch("/api/shares/declined-invitations", { cache: "no-store" });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    // SAFETY: the route returns DeclinedInvitation rows.
    return (await res.json()) as DeclinedInvitation[];
  },
});

/** People whose share invitations the user refuses (profile → unblock). */
export const invitationBlocksQueryOptions = queryOptions({
  queryKey: ["profile", "invitation-blocks"],
  queryFn: async (): Promise<InvitationBlock[]> => {
    const res = await fetch("/api/user/invitation-blocks", { cache: "no-store" });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    // SAFETY: the route returns InvitationBlock rows.
    return (await res.json()) as InvitationBlock[];
  },
});
